import { QUESTION_TYPES, type QuestionInput, type QuestionType } from "@mmm/contracts";
import { QUESTION_TYPE_LABEL_KEYS } from "../../constants.js";
import { Button, useT } from "@mmm/ui";
type AnswerOptionInput = QuestionInput["answerOptions"][number];
type RubricItemInput = QuestionInput["rubricItems"][number];

function nextOptionSortOrder(options: Array<{ sortOrder: number }>): number {
  return options.length === 0 ? 10 : Math.max(...options.map((o) => o.sortOrder)) + 10;
}

interface QuestionEditorProps {
  question: QuestionInput;
  onChange: (question: QuestionInput) => void;
  onDelete: () => void;
}

export function QuestionEditor({ question, onChange, onDelete }: QuestionEditorProps) {
  const t = useT();
  const update = (patch: Partial<QuestionInput>) => onChange({ ...question, ...patch });

  const setType = (type: QuestionType) => {
    if (type === "trueFalse") {
      update({
        type,
        correctAnswer: null,
        answerOptions: [
          { sortOrder: 10, text: t("tests.optionTrue"), isCorrect: true },
          { sortOrder: 20, text: t("tests.optionFalse"), isCorrect: false },
        ],
        rubricItems: [],
      });
    } else if (type === "freeText") {
      update({ type, correctAnswer: question.correctAnswer || "", answerOptions: [] });
    } else {
      update({
        type,
        correctAnswer: null,
        answerOptions:
          question.answerOptions.length >= 2
            ? question.answerOptions
            : [
                { sortOrder: 10, text: t("tests.optionA"), isCorrect: true },
                { sortOrder: 20, text: t("tests.optionB"), isCorrect: false },
              ],
      });
    }
  };

  const updateOption = (sortOrder: number, patch: Partial<AnswerOptionInput>) => {
    update({
      answerOptions: question.answerOptions.map((option) =>
        option.sortOrder === sortOrder ? { ...option, ...patch } : option
      ),
    });
  };

  const setCorrectOption = (sortOrder: number) => {
    update({
      answerOptions: question.answerOptions.map((option) => ({
        ...option,
        isCorrect: question.type === "multiSelect" ? option.isCorrect : option.sortOrder === sortOrder,
      })),
    });
  };

  const toggleMultiSelectCorrect = (sortOrder: number) => {
    const current = question.answerOptions.find((o) => o.sortOrder === sortOrder);
    updateOption(sortOrder, { isCorrect: !current?.isCorrect });
  };

  const addOption = () => {
    update({
      answerOptions: [
        ...question.answerOptions,
        { sortOrder: nextOptionSortOrder(question.answerOptions), text: "", isCorrect: false },
      ],
    });
  };

  const removeOption = (sortOrder: number) => {
    update({ answerOptions: question.answerOptions.filter((o) => o.sortOrder !== sortOrder) });
  };

  const addRubricItem = () => {
    update({
      rubricItems: [...question.rubricItems, { sortOrder: nextOptionSortOrder(question.rubricItems), text: "", points: 1 }],
    });
  };

  const updateRubricItem = (sortOrder: number, patch: Partial<RubricItemInput>) => {
    update({
      rubricItems: question.rubricItems.map((item) => (item.sortOrder === sortOrder ? { ...item, ...patch } : item)),
    });
  };

  const removeRubricItem = (sortOrder: number) => {
    update({ rubricItems: question.rubricItems.filter((item) => item.sortOrder !== sortOrder) });
  };

  return (
    <div className="mmm-card" style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <span style={{ fontWeight: 700, paddingTop: 10 }}>
          {t("tests.questionNumberDot", { number: question.questionNumber })}
        </span>
        <select
          className="mmm-select"
          style={{ width: 160 }}
          value={question.type}
          onChange={(e) => setType(e.target.value as QuestionType)}
        >
          {QUESTION_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(QUESTION_TYPE_LABEL_KEYS[type])}
            </option>
          ))}
        </select>
        <Button variant="danger" onClick={onDelete}>
          {t("common.delete")}
        </Button>
      </div>

      <textarea
        className="mmm-textarea"
        style={{ minHeight: 60 }}
        placeholder={t("tests.questionPromptPlaceholder")}
        value={question.prompt}
        onChange={(e) => update({ prompt: e.target.value })}
      />

      {question.type === "freeText" ? (
        <>
          <label className="mmm-field-label" style={{ marginTop: 10 }}>
            {t("tests.modelAnswer")}
          </label>
          <textarea
            className="mmm-textarea"
            style={{ minHeight: 60 }}
            value={question.correctAnswer || ""}
            onChange={(e) => update({ correctAnswer: e.target.value })}
          />

          <label className="mmm-field-label" style={{ marginTop: 10 }}>
            {t("tests.rubricItems")}
          </label>
          {question.rubricItems.map((item) => (
            <div key={item.sortOrder} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <input
                className="mmm-input"
                style={{ flex: 1 }}
                value={item.text}
                onChange={(e) => updateRubricItem(item.sortOrder, { text: e.target.value })}
              />
              <input
                type="number"
                min="1"
                className="mmm-input"
                style={{ width: 80 }}
                value={item.points}
                onChange={(e) => updateRubricItem(item.sortOrder, { points: Number(e.target.value) })}
              />
              <button
                type="button"
                aria-label={t("common.delete")}
                onClick={() => removeRubricItem(item.sortOrder)}
                style={{ color: "var(--mmm-muted)" }}
              >
                ×
              </button>
            </div>
          ))}
          <Button variant="secondary" onClick={addRubricItem}>
            {t("tests.addRubricItem")}
          </Button>
        </>
      ) : (
        <>
          <label className="mmm-field-label" style={{ marginTop: 10 }}>
            {t("tests.options")}
          </label>
          {question.answerOptions.map((option) => (
            <div key={option.sortOrder} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <input
                type={question.type === "multiSelect" ? "checkbox" : "radio"}
                checked={option.isCorrect}
                onChange={() =>
                  question.type === "multiSelect" ? toggleMultiSelectCorrect(option.sortOrder) : setCorrectOption(option.sortOrder)
                }
              />
              <input
                className="mmm-input"
                style={{ flex: 1 }}
                value={option.text}
                disabled={question.type === "trueFalse"}
                onChange={(e) => updateOption(option.sortOrder, { text: e.target.value })}
              />
              {question.type !== "trueFalse" && (
                <button
                  type="button"
                  aria-label={t("common.delete")}
                  onClick={() => removeOption(option.sortOrder)}
                  style={{ color: "var(--mmm-muted)" }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {question.type !== "trueFalse" && (
            <Button variant="secondary" onClick={addOption}>
              {t("tests.addOption")}
            </Button>
          )}
        </>
      )}

      <label className="mmm-field-label" style={{ marginTop: 10 }}>
        {t("tests.explanation")}
      </label>
      <textarea
        className="mmm-textarea"
        style={{ minHeight: 50 }}
        value={question.explanation}
        onChange={(e) => update({ explanation: e.target.value })}
      />
    </div>
  );
}
